import { useState } from "react";
import { Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { api, getApiError } from "@/lib/api";


export const DeleteAccountSection = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const deleteAccount = async () => {
    if (!password || busy) {
      return;
    }
    try {
      setBusy(true);
      await api.delete("/auth/account", { data: { password } });
      logout();
      toast.success("Je account en alle gegevens zijn verwijderd.");
      navigate("/signup");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-[28px] border border-red-200 bg-red-50/60 p-4" data-testid="profile-danger-zone">
      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-red-600">
        Account verwijderen
      </p>
      <p className="mt-2 text-sm text-stone-600">
        Verwijder je account en alle bijbehorende gegevens (weekplannen, pantry, boodschappenlijsten, presets en kalenderkoppelingen). Dit kan niet ongedaan worden gemaakt.
      </p>
      <AlertDialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) { setPassword(""); } }}>
        <AlertDialogTrigger asChild>
          <Button variant="outline" className="mt-4 h-12 w-full rounded-full border-red-200 bg-white text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="profile-delete-account-button">
            <Trash2 className="mr-2 h-4 w-4" />
            Account verwijderen
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="rounded-[28px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Account definitief verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Bevestig met je wachtwoord. Al je gegevens worden direct en permanent gewist.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input type="password" autoComplete="current-password" placeholder="Je wachtwoord" value={password} onChange={(event) => setPassword(event.target.value)} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="delete-account-password-input" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel-button">Annuleren</AlertDialogCancel>
            <Button className="rounded-full bg-red-600 text-white hover:bg-red-700" onClick={deleteAccount} disabled={busy || !password} data-testid="delete-account-confirm-button">
              {busy ? "Verwijderen..." : "Definitief verwijderen"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
